import { BusinessUsersService } from "@/app/core/apis/business-users.service";
import { isPaymentProviderReadOverrideAllowed } from "@/app/core/config/payment-feature-flags";
import { BusinessUsers } from "@/app/core/entities/business-users.entity";
import { PermissionChecker } from "@/app/core/services/permission-checker.service";
import { CanonicalPaymentReadOverrideService } from "@/app/core/services/canonical-payment-read-override.service";
import { KYCStatus } from "@/app/core/entities/business.entity";
import { loadCurrentUser, selectBusiness } from "@/app/stores/auth/auth.actions";
import { selectorBusinessId, selectorCurrentUser } from "@/app/stores/auth/auth.selectors";
import { StoreAuthModule } from "@/app/stores/auth/store-auth.module";
import { CommonModule, DatePipe } from "@angular/common";
import { Component, DestroyRef, inject, Input } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { NzMessageModule, NzMessageService } from "ng-zorro-antd/message";
import { ActivatedRoute, Router, RouterLink, RouterModule } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { ELoadingStatus } from "@core/models/loading-status.model";
import { Store } from "@ngrx/store";
import { NzAvatarModule } from "ng-zorro-antd/avatar";
import { NzBadgeModule } from "ng-zorro-antd/badge";
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzEmptyModule } from "ng-zorro-antd/empty";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzSwitchModule } from "ng-zorro-antd/switch";
import { NzPopconfirmModule } from "ng-zorro-antd/popconfirm";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { TruncatePipe, TRUNCATE_LENGTH } from "@core/pipes/truncate.pipe";
import { take, tap } from "rxjs";
import { loadNotifications, loadUnreadCount, markAllNotificationsAsRead, markNotificationAsRead } from "@/app/stores/notifications/notifications.actions";
import { selectorMarkAllReadInFlight, selectorNotifications, selectorUnreadCount } from "@/app/stores/notifications/notifications.selectors";
import { StoreNotificationsModule } from "@/app/stores/notifications/store-notifications.module";
import { Notification } from "@core/apis/notification.service";
import {
  getNotificationListBody,
  getNotificationListTitle,
  getNotificationNavigateUrl,
} from "@/app/core/notifications/notification-list.helpers";
import { selectorConversations } from "@/app/stores/messages/messages.selectors";
import { StoreMessagesModule } from "@/app/stores/messages/store-messages.module";
import { CountUnreadMessagesPipe } from "@/app/core/pipes/filter-conversations-with-pending-requests.pipe";
import posthog from "posthog-js";
import { User } from "@/app/core/entities/user.entity";
import { Business } from "@/app/core/entities/business.entity";

const UUID_V4ISH_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

@Component({
  selector: "app-header",
  imports: [
    NzDropDownModule,
    RouterLink,
    StoreAuthModule,
    CommonModule,
    FormsModule,
    RouterModule,
    NzDrawerModule,
    NzToolTipModule,
    NzBadgeModule,
    NzEmptyModule,
    NzIconModule,
    NzAvatarModule,
    NzSwitchModule,
    NzPopconfirmModule,
    StoreNotificationsModule,
    DatePipe,
    TruncatePipe,
    StoreMessagesModule,
    CountUnreadMessagesPipe,
    NzMessageModule,
  ],
  templateUrl: "./header.component.html",
  styles: [
    `
      :host ::ng-deep {
        .ant-badge-dot {
          width: 8px;
          height: 8px;
        }
        .ant-dropdown-menu {
          padding: 0 !important;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
          border-radius: 8px !important;
          overflow: hidden !important;
        }
      }
    `,
  ],
})
export class HeaderComponent {
  @Input() showNav = true;
  @Input() navItems: any[] = [];
  @Input() needsBeneficiarySetup: boolean | null = false;
  today = new Date();
  readonly auth0 = inject(AuthService);
  readonly store = inject(Store);
  readonly permissionChecker = inject(PermissionChecker);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly destroyRef = inject(DestroyRef);
  readonly canonicalReadOverride = inject(CanonicalPaymentReadOverrideService);
  readonly message = inject(NzMessageService);
  readonly isPaymentProviderReadOverrideAllowed = isPaymentProviderReadOverrideAllowed;
  readonly user$ = this.store.select(selectorCurrentUser).pipe(
    tap((user) => {
      if (!user?.businessUsers?.length) {
        return;
      }
      // Deferred: dispatching synchronously here (triggered by the async pipe
      // during this component's change detection) mutates store state that a
      // sibling binding in the parent template (currentRole$) already read in
      // the same CD cycle, causing NG0100.
      //
      // After Xero OAuth (full-page return) the in-memory auth businessId is
      // lost. Multi-business users never auto-selected before, so currentRole$
      // stayed empty and nav (Projects, Clients, …) disappeared.
      queueMicrotask(() => this.hydrateSelectedBusiness(user));
    })
  );
  readonly KYCStatus = KYCStatus;

  businessUsersService = inject(BusinessUsersService);

  ELoadingStatus = ELoadingStatus;
  readonly TRUNCATE = TRUNCATE_LENGTH;

  businessId = this.route.snapshot.params["businessId"];

  notifications$ = this.store.select(selectorNotifications);
  notificationsSignal = toSignal(this.notifications$, { initialValue: [] as Notification[] });
  unreadCount$ = this.store.select(selectorUnreadCount);
  markAllReadInFlight$ = this.store.select(selectorMarkAllReadInFlight);
  desktopNotificationVisible = false;
  mobileNotificationVisible = false;
  notificationDropdownVisible = false;
  conversations$ = this.store.select(selectorConversations);

  hasNotifications = true;

  notificationTitle(n: Notification): string {
    return getNotificationListTitle(n);
  }

  notificationBody(n: Notification): string | null {
    return getNotificationListBody(n);
  }

  markAllNotificationsAsReadConfirmed(): void {
    const businessId = this.businessId || this.resolveBusinessId();
    if (!businessId) return;
    this.store.dispatch(markAllNotificationsAsRead({ businessId }));
  }

  onNavItemClick(item: any) {
    const isRestricted = this.needsBeneficiarySetup && item.name !== "Dashboard" && item.name !== "Wallets";
    if (isRestricted) {
      this.message.warning("Please set up your beneficiary bank account first to unlock all features.", { nzDuration: 3500 });
      return;
    }
    if (this.needsBeneficiarySetup && item.name === "Wallets") {
      this.router.navigate([item.route], { queryParams: { tab: "beneficiary" } });
      return;
    }
    this.router.navigate([item.route]);
  }

  profile() {
    this.router.navigate([`/${this.businessId}/profile`]);
  }

  logout() {
    posthog.reset();
    this.auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  ngOnInit() {
    this.store.dispatch(loadCurrentUser());
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.canonicalReadOverride.applyFromQueryParamMap(params);
    });
    this.businessId = this.resolveBusinessId();
    if (this.businessId) {
      this.store.dispatch(loadUnreadCount({ businessId: this.businessId }));
      this.store.dispatch(loadNotifications({ query: { businessId: this.businessId, limit: 20 } }));
    }
  }

  get paymentProviderReadsChecked(): boolean {
    return this.canonicalReadOverride.getCurrentOverride() === true;
  }

  onPaymentProviderReadsToggle(checked: boolean): void {
    this.canonicalReadOverride.setOverride(checked);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { canonicalReads: checked ? "true" : "false" },
      queryParamsHandling: "merge",
    });
  }

  async onNotificationClick(notificationId: string) {
    const notification = this.notificationsSignal().find((n) => n.id === notificationId);
    if (!notification) return;

    this.store.dispatch(markNotificationAsRead({ notificationId }));
    const businessId = this.businessId || this.resolveBusinessId();
    if (!businessId) {
      this.desktopNotificationVisible = false;
      this.mobileNotificationVisible = false;
      return;
    }

    const targetUrl = getNotificationNavigateUrl(notification, businessId);
    if (targetUrl) {
      await this.router.navigateByUrl(targetUrl);
    }
    this.desktopNotificationVisible = false;
    this.mobileNotificationVisible = false;
  }

  selectBusiness(businessUser: BusinessUsers) {
    if (!businessUser.business?.id) {
      return;
    }
    if (!businessUser.isActive) {
      this.businessUsersService.activateUser(businessUser.business.id).subscribe(() => {
        this.selectAndNavigate(businessUser);
      });
    } else {
      this.selectAndNavigate(businessUser);
    }
  }

  selectAndNavigate(businessUser: BusinessUsers) {
    if (!businessUser.business) {
      return;
    }
    this.store.dispatch(selectBusiness({ business: businessUser.business }));
    // this.router.navigate([`/${businessUser.business.id}/dashboard`]);
  }

  /** KYC gate for nav: use the business in the URL/store, not businessUsers[0]. */
  businessForNav(user: User | null | undefined): Business | undefined {
    const businessId = this.resolveBusinessId();
    if (businessId && user?.businessUsers?.length) {
      const match = user.businessUsers.find((bu) => bu.businessId === businessId || bu.business?.id === businessId);
      if (match?.business) {
        return match.business;
      }
    }
    return user?.businessUsers?.[0]?.business;
  }

  private hydrateSelectedBusiness(user: User) {
    const routeBusinessId = this.resolveBusinessId();
    const memberships = user.businessUsers ?? [];

    if (routeBusinessId) {
      const match = memberships.find((bu) => bu.businessId === routeBusinessId || bu.business?.id === routeBusinessId);
      if (!match?.business) {
        void this.router.navigate(["/select-business"]);
        return;
      }

      this.store
        .select(selectorBusinessId)
        .pipe(take(1))
        .subscribe((currentId) => {
          if (currentId === match.business.id) {
            return;
          }
          // Route is already correct; only restore store context (skip activate side-effects on cold load).
          this.selectAndNavigate(match);
        });
      return;
    }

    if (memberships.length === 1 && memberships[0]?.business) {
      this.store
        .select(selectorBusinessId)
        .pipe(take(1))
        .subscribe((currentId) => {
          if (currentId === memberships[0].business.id) {
            return;
          }
          this.selectBusiness(memberships[0]);
        });
    }
  }

  private resolveBusinessId(): string | null {
    const routeBusinessId = this.route.snapshot.params["businessId"];
    if (routeBusinessId && UUID_V4ISH_REGEX.test(routeBusinessId)) {
      return routeBusinessId;
    }

    const firstPathSegment = this.router.url.split("/").filter(Boolean)[0];
    if (firstPathSegment && UUID_V4ISH_REGEX.test(firstPathSegment)) {
      return firstPathSegment;
    }

    return null;
  }
}
